import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardPaste } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@shared/ui/button';
const OTP_LENGTH = 6;
export function parsePastedOtp(text, length = OTP_LENGTH) {
    const digits = text.replace(/\D/g, '').slice(0, length);
    return Array.from({ length }, (_, i) => digits[i] ?? '');
}
export const OtpVerificationView = ({ onVerify, onResend, identifier }) => {
    const { t } = useTranslation('auth');
    const [otp, setOtp] = useState(() => Array(OTP_LENGTH).fill(''));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPasting, setIsPasting] = useState(false);
    const inputs = useRef([]);
    const isEmail = identifier.includes('@');
    const focusAfterFill = useCallback((next) => {
        const filled = next.filter(Boolean).length;
        const focusIndex = Math.min(Math.max(filled - 1, 0), OTP_LENGTH - 1);
        inputs.current[focusIndex]?.focus();
    }, []);
    const applyOtpFromText = useCallback((raw) => {
        const next = parsePastedOtp(raw);
        setOtp(next);
        focusAfterFill(next);
        return next;
    }, [focusAfterFill]);
    const handleChange = (index, value) => {
        const digitChars = value.replace(/\D/g, '');
        if (digitChars.length >= 2) {
            applyOtpFromText(value);
            return;
        }
        const digit = digitChars.slice(-1);
        if (digit && !/^\d$/.test(digit))
            return;
        if (!digit && value.length > 0)
            return;
        const newOtp = [...otp];
        newOtp[index] = digit;
        setOtp(newOtp);
        if (digit && index < OTP_LENGTH - 1) {
            inputs.current[index + 1]?.focus();
        }
    };
    const handlePaste = (e) => {
        e.preventDefault();
        applyOtpFromText(e.clipboardData.getData('text'));
    };
    const handlePasteClick = async () => {
        if (isSubmitting || isPasting)
            return;
        setIsPasting(true);
        try {
            const text = await navigator.clipboard.readText();
            applyOtpFromText(text);
        }
        catch {
            toast.error(t('otp.pasteError'));
            inputs.current[0]?.focus();
        }
        finally {
            setIsPasting(false);
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputs.current[index - 1]?.focus();
        }
    };
    const handleApply = async () => {
        const code = otp.join('');
        if (code.length < OTP_LENGTH)
            return;
        setIsSubmitting(true);
        try {
            await onVerify(code);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    useEffect(() => {
        inputs.current[0]?.focus();
    }, []);
    return (_jsxs("div", { className: "space-y-6 py-4", children: [_jsxs("div", { className: "text-center space-y-2", children: [_jsx("h2", { id: "otp-heading", className: "text-2xl font-bold text-white", children: isEmail ? t('otp.verifyEmail') : t('otp.verifyPhone') }), _jsxs("p", { className: "text-sm text-muted-foreground", children: [t('otp.sentTo'), " ", _jsx("br", {}), _jsx("span", { className: "text-white font-medium", children: identifier })] })] }), _jsxs("div", { role: "group", "aria-labelledby": "otp-heading", className: "space-y-2", children: [_jsx("div", { className: "flex justify-between gap-2", children: otp.map((digit, i) => (_jsx("input", { ref: (el) => {
                                inputs.current[i] = el;
                            }, type: "text", inputMode: "numeric", autoComplete: i === 0 ? 'one-time-code' : 'off', maxLength: 1, value: digit, "aria-label": t('otp.digit', { index: i + 1 }), onChange: (e) => handleChange(i, e.target.value), onPaste: handlePaste, onKeyDown: (e) => handleKeyDown(i, e), className: "w-full h-14 bg-white/5 border border-white/10 rounded-xl text-center text-xl font-bold text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" }, i))) }), _jsx("div", { className: "flex justify-end", children: _jsxs(Button, { type: "button", variant: "ghost", size: "sm", disabled: isSubmitting || isPasting, onClick: handlePasteClick, "aria-label": t('otp.pasteAria'), className: "gap-1.5 text-muted-foreground hover:text-primary", children: [_jsx(ClipboardPaste, { className: "h-4 w-4 shrink-0", "aria-hidden": true }), isPasting ? t('otp.pasting') : t('otp.paste')] }) })] }), _jsxs("div", { className: "space-y-4 pt-4", children: [_jsx(Button, { onClick: handleApply, disabled: isSubmitting || otp.some((d) => !d), className: "w-full h-14 rounded-2xl font-bold", variant: "primary", children: isSubmitting ? t('otp.verifying') : t('otp.confirm') }), _jsx("div", { className: "text-center", children: _jsx("button", { type: "button", onClick: onResend, className: "text-sm text-muted-foreground hover:text-primary transition-colors", children: t('otp.resend') }) })] })] }));
};
